import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Zap, MapPin, Shield, Wifi, Sun, Moon } from "lucide-react";
import { CHARGER_IMAGES } from "../constants/chargerImages";

// MILE PLUS Branded Station Mockup Component
const BrandedStation = ({ variant = "highway", className = "" }) => {
  const variants = {
    highway: {
      bg: "from-sky-900 via-sky-800 to-sky-950",
      ground: "from-gray-700 to-gray-800",
      title: "Highway Station",
      subtitle: "Mumbai-Nashik Expressway",
    },
    urban: {
      bg: "from-slate-800 via-slate-900 to-slate-950",
      ground: "from-gray-600 to-gray-700",
      title: "Urban Hub",
      subtitle: "Nashik City Center",
    },
    mall: {
      bg: "from-indigo-900 via-purple-900 to-slate-950",
      ground: "from-gray-500 to-gray-600",
      title: "Mall Parking",
      subtitle: "Premium Location",
    },
    night: {
      bg: "from-slate-950 via-slate-900 to-black",
      ground: "from-gray-800 to-gray-900",
      title: "24/7 Station",
      subtitle: "Always Available",
    },
  };

  const config = variants[variant] || variants.highway;

  return (
    <div className={`relative rounded-2xl overflow-hidden ${className}`}>
      {/* Sky/Background */}
      <div className={`absolute inset-0 bg-gradient-to-b ${config.bg}`} />
      
      {/* Stars for night variant */}
      {variant === "night" && (
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
              style={{
                top: `${Math.random() * 40}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Content Container */}
      <div className="relative h-full flex flex-col">
        {/* Station Structure */}
        <div className="flex-1 flex items-end justify-center px-4 pb-0">
          {/* Canopy Structure */}
          <div className="relative">
            {/* Main Canopy */}
            <div className="relative">
              {/* Canopy Roof */}
              <div className="w-80 h-4 bg-gradient-to-b from-gray-200 to-gray-300 rounded-t-lg shadow-lg" />
              <div className="w-80 h-2 bg-primary shadow-[0_0_20px_rgba(204,255,0,0.5)]" />
              
              {/* MILE PLUS Branding on Canopy */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-6 py-2 bg-black/80 rounded-lg border border-primary/50">
                <span 
                  className="font-heading font-black text-xl tracking-[0.2em]"
                  style={{
                    background: 'linear-gradient(135deg, #CCFF00 0%, #E8FF66 50%, #CCFF00 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  MILE PLUS
                </span>
              </div>

              {/* Support Pillars */}
              <div className="flex justify-between px-4">
                <div className="w-3 h-32 bg-gradient-to-b from-gray-300 to-gray-400 rounded-b" />
                <div className="w-3 h-32 bg-gradient-to-b from-gray-300 to-gray-400 rounded-b" />
              </div>
            </div>

            {/* Chargers Under Canopy */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-8">
              {/* Charger 1 */}
              <div className="relative">
                <div className="w-16 h-28 bg-gradient-to-b from-white to-gray-100 rounded-lg shadow-xl border-2 border-primary/30">
                  {/* Screen */}
                  <div className="absolute top-2 left-2 right-2 h-8 bg-black rounded">
                    <div className="w-full h-full flex items-center justify-center">
                      <Zap className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                  {/* MILE PLUS Logo */}
                  <div className="absolute top-12 left-0 right-0 text-center">
                    <span className="text-[8px] font-bold text-primary tracking-wider">MILE PLUS</span>
                  </div>
                  {/* Charging Cable */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-2 h-6 bg-gray-800 rounded-full" />
                </div>
                {/* Status Light */}
                <div className="absolute -top-1 right-1 w-2 h-2 bg-primary rounded-full animate-pulse shadow-[0_0_10px_rgba(204,255,0,0.8)]" />
                {/* Power Label */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-primary font-bold whitespace-nowrap">
                  60 kW
                </div>
              </div>

              {/* Charger 2 - Larger */}
              <div className="relative">
                <div className="w-20 h-36 bg-gradient-to-b from-white to-gray-100 rounded-lg shadow-xl border-2 border-secondary/30">
                  {/* Screen */}
                  <div className="absolute top-3 left-2 right-2 h-10 bg-black rounded">
                    <div className="w-full h-full flex items-center justify-center">
                      <Zap className="w-5 h-5 text-secondary" />
                    </div>
                  </div>
                  {/* MILE PLUS Logo */}
                  <div className="absolute top-16 left-0 right-0 text-center">
                    <span className="text-[10px] font-bold text-secondary tracking-wider">MILE PLUS</span>
                  </div>
                  {/* Dual Cables */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    <div className="w-2 h-8 bg-gray-800 rounded-full" />
                    <div className="w-2 h-8 bg-gray-800 rounded-full" />
                  </div>
                </div>
                {/* Status Light */}
                <div className="absolute -top-1 right-1 w-3 h-3 bg-secondary rounded-full animate-pulse shadow-[0_0_10px_rgba(0,212,255,0.8)]" />
                {/* Power Label */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-secondary font-bold whitespace-nowrap">
                  120 kW
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ground */}
        <div className={`h-20 bg-gradient-to-b ${config.ground}`}>
          {/* Parking Lines */}
          <div className="h-full flex items-start pt-2 px-8 gap-4">
            <div className="flex-1 h-1 bg-yellow-400/60 rounded" />
            <div className="flex-1 h-1 bg-yellow-400/60 rounded" />
          </div>
          {/* Ground Markings */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-xs text-white/60 font-medium">EV CHARGING ONLY</span>
          </div>
        </div>

        {/* Station Info Overlay */}
        <div className="absolute top-4 left-4 glass rounded-lg px-3 py-2">
          <p className="text-primary font-heading text-sm">{config.title}</p>
          <p className="text-foreground-muted text-xs">{config.subtitle}</p>
        </div>

        {/* Features Badge */}
        <div className="absolute top-4 right-4 flex gap-2">
          <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center" title="24/7">
            {variant === "night" ? <Moon className="w-4 h-4 text-secondary" /> : <Sun className="w-4 h-4 text-yellow-400" />}
          </div>
          <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center" title="WiFi">
            <Wifi className="w-4 h-4 text-primary" />
          </div>
          <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center" title="Secure">
            <Shield className="w-4 h-4 text-green-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

const StationShowcase = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section
      id="station-showcase"
      className="py-24 md:py-32 bg-background-paper relative overflow-hidden"
      data-testid="station-showcase-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Station Design
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            <span className="text-primary">MILE PLUS</span> BRANDED
            <br />
            CHARGING STATIONS
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            Premium charging infrastructure with distinctive MILE PLUS branding.
            Recognizable, reliable, and designed for the future of mobility.
          </p>
        </motion.div>

        {/* Station Mockups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Highway Station */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <BrandedStation variant="highway" className="h-80" />
          </motion.div>

          {/* Night Station */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <BrandedStation variant="night" className="h-80" />
          </motion.div>

          {/* Urban Station */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <BrandedStation variant="urban" className="h-80" />
          </motion.div>

          {/* Mall Station */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <BrandedStation variant="mall" className="h-80" />
          </motion.div>
        </div>

        {/* Real Installation Images */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16"
        >
          <h3 className="font-heading text-2xl text-foreground text-center mb-8">
            Real <span className="text-primary">Installations</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { src: CHARGER_IMAGES.installations.ahmedabad, label: "Ahmedabad" },
              { src: CHARGER_IMAGES.installations.bangalore1, label: "Bangalore" },
              { src: CHARGER_IMAGES.installations.pune, label: "Pune" },
              { src: CHARGER_IMAGES.installations.noida, label: "Noida" },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="relative rounded-xl overflow-hidden group aspect-video"
              >
                <img
                  src={item.src}
                  alt={`${item.label} Installation`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                {/* MILE PLUS Branding Overlay */}
                <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 rounded border border-primary/30">
                  <span className="text-primary text-xs font-bold tracking-wider">MILE PLUS</span>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-medium text-sm">{item.label}</p>
                  <p className="text-white/60 text-xs">Operational</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { icon: Zap, label: "Fast Charging", value: "Up to 240 kW" },
            { icon: Shield, label: "Secure", value: "24/7 CCTV" },
            { icon: Wifi, label: "Connected", value: "Smart Monitoring" },
            { icon: MapPin, label: "Accessible", value: "Prime Locations" },
          ].map((feature, index) => (
            <div
              key={feature.label}
              className="glass-card text-center group hover:border-primary/50 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                <feature.icon className="w-6 h-6 text-primary" />
              </div>
              <p className="text-foreground font-medium">{feature.label}</p>
              <p className="text-primary text-sm mt-1">{feature.value}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default StationShowcase;
